import { cn } from '@/lib/utils';
import { computeAttentionCurve, isPreviewCurve } from '@/utils/attentionCurve';
import type { Scorecard } from '@/types/analysis';

type AttentionCurveChartProps = {
  scriptId?: string;
  scorecard?: Scorecard | null;
  className?: string;
};

export default function AttentionCurveChart({ scriptId, scorecard, className }: AttentionCurveChartProps) {
  const points = computeAttentionCurve(scorecard ?? null);
  const preview = isPreviewCurve(points, scorecard ?? null);
  const series = points ?? [42, 48, 55, 62, 58, 71, 78, 82, 76, 88, 85, 90];

  const w = 400;
  const h = 120;
  const pad = 8;
  const max = Math.max(...series);
  const min = Math.min(...series);
  const range = max - min || 1;

  const polyline = series
    .map((v, i) => {
      const x = pad + (i / (series.length - 1)) * (w - pad * 2);
      const y = h - pad - ((v - min) / range) * (h - pad * 2);
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <div className={className}>
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <p className="ni-label">Attention curve</p>
          {scriptId && <p className="text-[10px] font-mono text-white/35 mt-1">{scriptId}</p>}
        </div>
        <span className={cn('text-[10px] font-mono uppercase', preview ? 'text-white/30' : 'text-index-cleared')}>
          {preview ? 'Neuro-model v3.1 · Preview' : 'Neuro-model v3.1 · Live'}
        </span>
      </div>
      <div className="ni-panel-deep p-4">
        <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-28" preserveAspectRatio="none">
          <line
            x1={pad}
            y1={h / 2}
            x2={w - pad}
            y2={h / 2}
            stroke="rgba(255,255,255,0.08)"
            strokeDasharray="4 4"
          />
          <polyline fill="none" stroke="#4ade80" strokeWidth="2" points={polyline} />
        </svg>
        <div className="flex justify-between mt-2 text-[9px] font-mono uppercase text-white/25">
          <span>Act I</span>
          <span>Act II</span>
          <span>Midpoint</span>
          <span>Act III</span>
          <span>Climax</span>
        </div>
      </div>
    </div>
  );
}
